import React, { useState, useCallback, useRef } from "react";
import {
  loginWithCredentials as loginWithCredentialsApi,
  getSession,
  beginLoginFlow as apiBeginLoginFlow,
} from "@/api";
import useDevice from "@/hooks/useDevice";
import Mixpanel from "@/services/tracking";
import { getBankIdUrl } from "@/utils";
import { getReferrer, getStoredQueryParams } from "@/utils/queryParams";
import { useOnboarding } from "@/hooks/useOnboarding";
import { QUERY_PARAM_WHITELIST, STORAGE_KEY } from "@/constants";
import { getFlowConfig } from "@/data/flows";

const POLL_RATE_IN_MS = 1000;

const hasCookieConsent = (): boolean => {
  // @ts-ignore
  return window.Cookiebot?.consent?.marketing === true;
};

const getTimeUntilNextRequest = (requestTime: number) => {
  return POLL_RATE_IN_MS - requestTime > 0 ? POLL_RATE_IN_MS - requestTime : 0;
};

type LoginState =
  | "idle"
  | "enter_identifier"
  | "waiting_for_bankid"
  | "starting"
  | "polling"
  | "user_signing"
  | "authenticated"
  | "error";

type ContextValue = {
  sessionId?: string;
  session?: LoginResponse;
  completeLogin(refToken: string): Promise<void>;
  loginWithCredentials(identifier: string): Promise<void>;
  loginOnDevice(submission: WebOnboardingRegistrationSubmission): Promise<void>;
  logout(): void;
  beginQrLoginFlow(
    submission: WebOnboardingRegistrationSubmission,
  ): Promise<void>;
  cancelLogin: () => void;
  setLoginState: (newState: LoginState) => void;
  loginState: LoginState;
  activeQrCode?: Base64SvgImage;
  error?: unknown;
  friendlyError?: ErrorMessageDetails;
  dismissFriendlyError: () => void;
};

export const AuthContext = React.createContext<ContextValue | undefined>(
  undefined,
);

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const { currentOnboardingFlow, getAnswerRequestObject } = useOnboarding();
  const [session, setSessionRaw] = useState<LoginResponse | undefined>();
  const [activeQrCode, setActiveQrCode] = useState<Base64SvgImage>();
  const [sessionId, setSessionId] = useState<string>();
  const [loginState, _setLoginState] = useState<LoginState>("idle");
  const loginStateRef = useRef<LoginState>("idle");
  const [error, _setError] = useState<unknown>();
  const [friendlyError, setFriendlyError] = useState<ErrorMessageDetails>();
  const timeoutRef = useRef<number>();
  const abortControllerRef = useRef<AbortController>();
  const device = useDevice();

  const handleGetSessionError = async (res: Response) => {
    setLoginState("error");

    try {
      const message: ApiErrorMessage = (await res.json()) as ApiErrorMessage;

      if (message?.details) {
        setFriendlyError(message.details);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const beginQrLoginFlow = async (
    submission: WebOnboardingRegistrationSubmission,
  ) => {
    setLoginState("starting");

    submission.useQr = true;

    if (hasCookieConsent()) {
      submission.sourceParams = getStoredQueryParams(
        QUERY_PARAM_WHITELIST,
        null,
      );
      submission.referrer = getReferrer();
    }

    try {
      const start = new Date();
      const response = await apiBeginLoginFlow(submission);
      const finish = new Date();
      const requestDuration = finish.valueOf() - start.valueOf();
      setActiveQrCode(window.atob(response.QRCode));
      setLoginState("polling");
      timeoutRef.current = setTimeout(() => {
        void updateQrCode(response.sessionId);
      }, getTimeUntilNextRequest(requestDuration));
      setSessionId(response.sessionId);
    } catch (err) {
      setError(err);
      console.error(err);
    }
  };

  const updateQrCode = async (sessionId: string) => {
    try {
      abortControllerRef.current = new AbortController();
      const start = new Date();
      const res = await getSession(
        sessionId,
        abortControllerRef.current.signal,
      );

      if (!res.ok) {
        handleGetSessionError(res);
        return;
      }

      const response = (await res.json()) as BankIdPollingResponse;
      const finish = new Date();
      const requestDuration = finish.valueOf() - start.valueOf();
      const status = response?.sessionResponse?.grandidObject?.message?.status;
      const hintCode =
        response?.sessionResponse?.grandidObject?.message?.hintCode;

      if (hintCode === "userSign") {
        setLoginState("user_signing");
      }

      if (status === "failed") {
        if (hintCode === "userCancel") {
          setLoginState("idle");
        } else {
          setError("Login failed");
        }
      }

      if (response.loginResponse) {
        setSession(response.loginResponse as LoginResponse);
        setLoginState("authenticated");
        loginCompleted();
        return;
      }

      setActiveQrCode(
        window.atob(response.sessionResponse.grandidObject.QRCode),
      );

      if (["polling", "user_signing"].includes(loginStateRef.current)) {
        timeoutRef.current = setTimeout(() => {
          void updateQrCode(sessionId);
        }, getTimeUntilNextRequest(requestDuration));
      }
    } catch (err) {
      setError(err);
      console.error(err);
      clearTimeout(timeoutRef.current);
    }
  };

  const cancelLogin = () => {
    setLoginState("idle");
    setActiveQrCode(undefined);
    clearError();
    clearTimeout(timeoutRef.current);
    abortControllerRef.current?.abort("Login canceled");
  };

  const setError = useCallback((err: unknown) => {
    _setError(err);
    console.error(err);
    setLoginState("error");
  }, []);

  const clearError = () => {
    _setError(undefined);
    setLoginState("idle");
  };

  const setLoginState = (newState: LoginState) => {
    _setLoginState(newState);
    loginStateRef.current = newState;
  };

  const loginOnDevice = async (
    submission: WebOnboardingRegistrationSubmission,
  ) => {
    try {
      submission.useQr = false;

      if (hasCookieConsent()) {
        submission.sourceParams = getStoredQueryParams(
          QUERY_PARAM_WHITELIST,
          null,
        );
        submission.referrer = getReferrer();
      }

      setLoginState("waiting_for_bankid");

      const response = await apiBeginLoginFlow(submission);

      localStorage.setItem("sessionId", response.sessionId);
      setSessionId(response.sessionId);

      const url = getBankIdUrl(response.autoStartToken, device.isAppleDevice);
      console.log("Attempting to open BankID");
      Mixpanel.track("OpenBankID");
      window.location.replace(url);
    } catch (e) {
      setError(e);
      console.error(e);
    }
  };

  const completeLogin = useCallback(
    async (sessionId: string) => {
      const res = await getSession(sessionId);

      if (!res.ok) {
        handleGetSessionError(res);
        return;
      }

      const response = (await res.json()) as BankIdPollingResponse;
      const status = response?.sessionResponse?.grandidObject?.message?.status;
      const hintCode =
        response?.sessionResponse?.grandidObject?.message?.hintCode;

      if (status === "failed") {
        if (hintCode === "userCancel") {
          setLoginState("idle");
        } else {
          setError("Login failed");
        }
      }

      if (response.loginResponse) {
        setSession(response.loginResponse as LoginResponse);
        setLoginState("authenticated");
        loginCompleted();
        return;
      }
    },
    [setError],
  );

  const loginWithCredentials = async (identifier: string) => {
    setLoginState("starting");

    const credentialsResponse = await loginWithCredentialsApi(
      identifier,
      getFlowConfig(currentOnboardingFlow)?.treatmentArea || "OBESITY",
      getAnswerRequestObject(),
      getStoredQueryParams(QUERY_PARAM_WHITELIST, null),
      getReferrer(),
    );

    if (!credentialsResponse.ok) {
      setLoginState("error");
      const error = new Error();
      if (credentialsResponse.status === 409) {
        error.message = "Conflict";
      }

      const message: ApiErrorMessage =
        (await credentialsResponse.json()) as ApiErrorMessage;

      if (message?.details) {
        setFriendlyError(message.details);
      }

      setError("Credentials login failed");
      throw error;
    }

    const loginResponseJson: LoginResponse =
      (await credentialsResponse.json()) as LoginResponse;

    setSession(loginResponseJson);
    loginCompleted();
  };

  const setSession = (loginResponse: LoginResponse) => {
    setSessionRaw(loginResponse);
  };

  const logout = () => {
    setSessionRaw(undefined);
  };

  const loginCompleted = () => {
    Mixpanel.track("OnboardingCompleted", { flow: currentOnboardingFlow });
    Mixpanel.register({ flow: null });
    // Clear stored onboarding state to allow the same device to complete both hypertension and obesity onboarding
    localStorage.removeItem(STORAGE_KEY);
  };

  const dismissFriendlyError = () => {
    setLoginState("idle");
    setFriendlyError(undefined);
  };

  const value = {
    session,
    sessionId,
    loginState,
    activeQrCode,
    error,
    friendlyError,
    completeLogin,
    loginWithCredentials,
    loginOnDevice,
    logout,
    beginQrLoginFlow,
    cancelLogin,
    setLoginState,
    dismissFriendlyError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
